/**
 * Domain types. These mirror the Sheets tabs one-for-one so that the Sheet
 * stays human-readable and remains the source of truth.
 *
 * Dates are stored and passed around as plain `YYYY-MM-DD` strings in the
 * workspace timezone. We never put a JS `Date` in the model: the whole app
 * reasons about calendar days, not instants, and `Date` would drag UTC
 * offsets into every comparison.
 */

export type IsoDate = string; // YYYY-MM-DD

export type DayType = 'Thursday' | 'Sunday';

export const DAY_TYPES: readonly DayType[] = ['Thursday', 'Sunday'] as const;

export type VolunteerStatus = 'Active' | 'Paused' | 'Removed';

export const VOLUNTEER_STATUSES: readonly VolunteerStatus[] = [
  'Active',
  'Paused',
  'Removed',
] as const;

export type Outcome =
  | 'Pending'
  | 'Completed'
  | 'Rider Canceled'
  | 'Volunteer Bailed'
  | 'No Meeting';

export const OUTCOMES: readonly Outcome[] = [
  'Pending',
  'Completed',
  'Rider Canceled',
  'Volunteer Bailed',
  'No Meeting',
] as const;

export type Legs = 'Both' | 'Pickup only' | 'Dropoff only';

export const LEGS: readonly Legs[] = ['Both', 'Pickup only', 'Dropoff only'] as const;

export interface Volunteer {
  id: string;
  name: string;
  email: string;
  phone: string;
  availableThursday: boolean;
  availableSunday: boolean;
  status: VolunteerStatus;
  /** Optional end of a seasonal pause. Blank = paused indefinitely. */
  pausedUntil: IsoDate | null;
  /** Set at onboarding to the rolling average so newcomers land mid-rotation. */
  seedCredit: number;
  /**
   * When the seed was written. The seed only counts while this date is inside
   * the rolling window, which is what makes it "age out naturally" (spec 3.3).
   * Blank = seed applies indefinitely (legacy rows).
   */
  seededOn: IsoDate | null;
  notes: string;
  /** 1-based row in the Volunteers tab. Absent for rows not yet written. */
  row?: number;
}

export interface Rider {
  id: string;
  name: string;
  notes: string;
  active: boolean;
  /** Optional. When set, the rider is invited to their own ride alongside the volunteer. */
  email: string;
  /** Optional. Used for the coordinator's "still good?" message. */
  phone: string;
  row?: number;
}

export interface Assignment {
  date: IsoDate;
  riderId: string;
  /** Blank = unassigned. */
  volunteerId: string | null;
  confirmed: boolean;
  outcome: Outcome;
  legs: Legs;
  /** Hand-picked or hand-edited: protected from regeneration. */
  manual: boolean;
  /** Event on the public calendar (title-only, never any guests). */
  publicEventId: string | null;
  /** Standalone event on the private calendar carrying the volunteer invite. */
  inviteEventId: string | null;
  notes: string;
  row?: number;
}

export interface Settings {
  /** Only outcomes within this many weeks count toward ranking (spec 3.4). */
  rollingWindowWeeks: number;
  /** How far ahead `Generate` runs by default. */
  coordinatorHorizonWeeks: number;
  /** Volunteers only see this far out; calendar sync stops here. */
  calendarSyncHorizonWeeks: number;
  meetingDays: DayType[];
  timezone: string;
  /**
   * Local meeting start time per day, HH:MM. Thursday and Sunday meetings
   * rarely start at the same hour, so each day carries its own time.
   */
  meetingTimes: Record<DayType, string>;
  meetingDurationMinutes: number;
  /** Location string put on calendar events. Blank = omitted. */
  meetingLocation: string;
  publicCalendarId: string | null;
  assignmentCalendarId: string | null;
}

export const DEFAULT_SETTINGS: Settings = {
  rollingWindowWeeks: 4,
  coordinatorHorizonWeeks: 13,
  calendarSyncHorizonWeeks: 4,
  meetingDays: ['Thursday', 'Sunday'],
  timezone: 'America/New_York',
  meetingTimes: { Thursday: '19:00', Sunday: '10:00' },
  meetingDurationMinutes: 120,
  meetingLocation: '',
  publicCalendarId: null,
  assignmentCalendarId: null,
};

/** Everything the client needs, fetched in one shot. */
export interface Workspace {
  spreadsheetId: string;
  spreadsheetUrl: string;
  volunteers: Volunteer[];
  riders: Rider[];
  assignments: Assignment[];
  settings: Settings;
  /** Server time as an ISO date in the workspace timezone; anchors "today". */
  today: IsoDate;
  /** Bumped on every successful write; lets the client detect staleness. */
  revision: string;
}

export function assignmentKey(date: IsoDate, riderId: string): string {
  return `${date}|${riderId}`;
}
